import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { ErrorhandlingService } from 'src/app/services/errorhandling.service';
import { TokenStorageService } from 'src/app/services/token-storage.service';
import { CdkDrag, CdkDragDrop, CdkDropList, moveItemInArray, transferArrayItem } from '@angular/cdk/drag-drop';
import { TaskComponent } from '../task/task.component';
import { createTask, TaskDTO } from 'src/app/interfaces/task';
import { CalendarDTO } from 'src/app/interfaces/calendar';
import { UserDTO } from 'src/app/interfaces/user';
import { firstValueFrom } from 'rxjs';
import { CalendarService } from 'src/app/services/calendar.service';
import { TaskService } from 'src/app/services/task.service';
import { NgForOf, NgStyle, NgTemplateOutlet, NgIf } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatOptionModule } from '@angular/material/core';
import { DatePipe } from '@angular/common';
import { MatFormFieldModule } from '@angular/material/form-field';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { SettingsDTO } from 'src/app/interfaces/settings';
import { SettingsService } from 'src/app/services/settings.service';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSelectModule } from '@angular/material/select';
import { FunctionsService } from 'src/app/services/functions.service';

@Component({
  selector: 'app-tasks',
  imports: [NgStyle, MatIconModule, CdkDrag, CdkDropList, MatOptionModule, DatePipe, NgForOf, NgTemplateOutlet, MatSelectModule,
    MatFormFieldModule, FormsModule, ReactiveFormsModule, MatInputModule, MatButtonModule, NgIf, MatMenuModule, MatTooltipModule],
  templateUrl: './tasks.component.html',
  styleUrls: ['./tasks.component.scss'],
})
export class TasksComponent implements OnInit {

  role: string;
  user: UserDTO;
  isOk: boolean = false;
  isLoggedIn = false;
  backlog: TaskDTO[] = [];
  inProgressTasks: TaskDTO[] = [];
  doneTasks: TaskDTO[] = [];
  onHoldTasks: TaskDTO[] = [];
  settings?: SettingsDTO;
  calendars: CalendarDTO[] = [];
  selectedCalendarForTaskOverview: CalendarDTO = {
    id: -1, userId: -1, calendarIntegrationId: -1, calendarIntegrationName: '',
    externalCalendarId: '', summary: '', showInDashboard: false, showInNotes: false, uuid: '', taskUuid: '', color: '', url: ''
  };
  filterCalendars: CalendarDTO[] = [];

  constructor(
    private calendarService: CalendarService, public matDialog: MatDialog, private tokenStorageService: TokenStorageService, private router: Router,
    private taskService: TaskService, private settingsService: SettingsService, public functions: FunctionsService) {
    this.getSettings();
  }

  async getSettings() {
    this.settings = await firstValueFrom(this.settingsService.getSettings());
  }

  async ngOnInit() {
    this.isLoggedIn = !!this.tokenStorageService.getToken();

    if (this.isLoggedIn) {
      this.user = this.tokenStorageService.getUser();

      this.getAllCalendars();
      this.getAllTasks();
    } else {
      this.router.navigate(['home']);
    }
  }

  async getAllCalendars() {
    this.calendars = await firstValueFrom(this.calendarService.getAllCalendars());
    this.filterCalendars = [...this.calendars];
    this.filterCalendars.unshift({
      id: -1,
      userId: this.user.id,
      calendarIntegrationId: -1,
      calendarIntegrationName: '',
      externalCalendarId: '',
      summary: '<All>',
      showInDashboard: false,
      showInNotes: false,
      uuid: '',
      taskUuid: '',
      color: '',
      url: ''
    });

  }

  getCalendarLabel(calendar: CalendarDTO): string {
    if (!calendar) return '';
    const duplicates = this.calendars.filter(c => c.summary === calendar.summary);
    if (duplicates.length > 1 && calendar.calendarIntegrationName) {
      return `${calendar.summary} (${calendar.calendarIntegrationName})`;
    }
    return calendar.summary;
  }

  onCalendarSelectChange(calendar: CalendarDTO | null): void {
    this.selectedCalendarForTaskOverview = calendar;
    this.getAllTasks();
  }

  getAllTasks() {
    this.getTasks("Not started");
    this.getTasks("In progress");
    if (this.settings && this.settings.showOnHold) {
      this.getTasks("On hold");
    }
    this.getTasks("Done");

  }

  async getTasks(status: string) {
    const data = await firstValueFrom(this.taskService.getTasks(this.selectedCalendarForTaskOverview.id, status));
    if (status.includes("Not started")) {
      if (data != null) {
        this.backlog = data;
      } else {
        this.backlog = [];
      }
    }
    if (status.includes("In progress")) {
      if (data != null) {
        this.inProgressTasks = data;
      } else {
        this.inProgressTasks = [];
      }
    }
    if (status.includes("On hold")) {
      if (data != null) {
        this.onHoldTasks = data;
      } else {
        this.onHoldTasks = [];
      }
    }
    if (status.includes("Done")) {
      if (data != null) {
        this.doneTasks = data;
      } else {
        this.doneTasks = [];
      }
    }
  }

  addTask() {
    const dialogConfig = new MatDialogConfig();
    // The user can't close the dialog by clicking outside its body
    dialogConfig.disableClose = false;
    dialogConfig.id = "modal-component";
    dialogConfig.panelClass = "squaredDialog";
    dialogConfig.height = "530px";
    dialogConfig.width = "950px";
    const modalDialog = this.matDialog.open(TaskComponent, dialogConfig);
    modalDialog.afterClosed().subscribe(data => {
      if (data && data.task) {
        var task = data.task;
        task.calendarId = this.selectedCalendarForTaskOverview.id;
        task.sequence = 0;
        this.saveTask(task, true);
      }
    });
  }

  editTask(task: TaskDTO) {
    if (task != null) {
      const dialogConfig = new MatDialogConfig();
      // The user can't close the dialog by clicking outside its body
      dialogConfig.disableClose = false;
      dialogConfig.id = "modal-component";
      dialogConfig.panelClass = "squaredDialog";
      dialogConfig.data = task;
      dialogConfig.height = "530px";
      dialogConfig.width = "950px";
      const modalDialog = this.matDialog.open(TaskComponent, dialogConfig);
      modalDialog.afterClosed().subscribe(data => {
        if (data) {
          var task = data.task;
          if (task != null) {
            this.saveTask(task, true);
          } else {
            this.reload();
          }
        }
      });
    }
  }

  reload() {
    this.getTasks("Not started");
    this.getTasks("In progress");
    if (this.settings && this.settings.showOnHold) {
      this.getTasks("On hold");
    }
    this.getTasks("Done");
  }

  async saveTask(task: TaskDTO, reload: boolean) {
    const data = await firstValueFrom(this.taskService.saveTask(task.id, createTask(task.calendarId, task.title, task.text, task.status, task.creationDate, task.dueDate, task.statusDate, task.priority, task.sequence, task.noteId)));
    if (data && reload) {
      this.reload();
    }
  }

  deleteTask(task: TaskDTO) {
    this.taskService.deleteTask(task);
  }

  drop(event: CdkDragDrop<TaskDTO[]>, newStatus: string) {
    if (event.previousContainer === event.container) {
      // 1. Item verplaatsen binnen dezelfde kolom (Reordering)
      moveItemInArray(
        event.container.data,
        event.previousIndex,
        event.currentIndex
      );
    } else {
      // 2. Item verplaatsen naar een ANDERE kolom (Status wijziging)
      transferArrayItem(
        event.previousContainer.data,
        event.container.data,
        event.previousIndex,
        event.currentIndex
      );

      // Update de status van het versleepte item direct
      const task = event.container.data[event.currentIndex];
      task.status = newStatus;
    }

    // 3. Update sequences en sla alles op in de doel-kolom
    event.container.data.forEach((task, index) => {
      task.sequence = index;
      // Alleen opslaan als het nodig is (of alles, afhankelijk van je backend)
      this.saveTask(task, false);
    });

    // 4. Optioneel: als je ook sequences in de vorige kolom moet fixen
    if (event.previousContainer !== event.container) {
      event.previousContainer.data.forEach((task, index) => {
        task.sequence = index;
        this.saveTask(task, false);
      });
    }
  }

  overDue(task: TaskDTO) {
    if (task.dueDate != null) {
      if (new Date(task.dueDate).getTime() < new Date().getTime()) {
        return Math.floor((new Date().getTime() - new Date(task.dueDate).getTime()) / 1000 / 60 / 60 / 24);
      } else {
        return 0;
      }
    } else return 0;
  }

  getPriorityColor(priority: string): string {
    switch (priority) {
      case "High": return '#f44336'; // Rood (High)
      case "": return '#ff9800'; // Oranje (Medium)
      case "Normal": return '#4caf50'; // Groen (Low)
      default: return '#bdbdbd'; // Grijs
    }
  }

    getTaskCardStyle(task: TaskDTO) {
    if (!task) return {};

    if (task.calendarColor) {
      return {
        'background-color': `${task.calendarColor}0d`, // 26 is hex voor ~15% transparantie
      };
    } else return {};
  }

}
